import { motion } from "framer-motion";
import { Download, FileText, ShieldCheck, BookOpen } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PDF_URL = `${API}/pdf/guide`;

const HIGHLIGHTS = [
  "Building disciplined SIPs that compound across cycles",
  "Goal-based investing — children's education, home, retirement",
  "Section 80C strategy: ELSS vs PPF vs NPS — choosing the right mix",
  "Asset allocation that explains 90% of long-term outcomes",
  "Eight common investor mistakes (and how to avoid them)",
];

export default function GuideDownload() {
  return (
    <section data-testid="guide-section" className="section relative bg-[#050505]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="relative rounded-3xl overflow-hidden border border-[#D4AF37]/30 bg-gradient-to-br from-[#1A1407] via-[#0a0a0a] to-[#050505] p-8 md:p-14">
          <div className="absolute -top-32 -right-20 w-[460px] h-[460px] rounded-full bg-[#D4AF37]/15 blur-[140px] pointer-events-none" />
          <div className="grid lg:grid-cols-12 gap-10 items-center relative">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="lg:col-span-7"
            >
              <p className="text-xs tracking-[0.35em] uppercase text-[#D4AF37]">Investor Education</p>
              <h2 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl text-white mt-4 leading-[1.05]">
                The Axomind <span className="text-gold-gradient">Investor Education Guide</span>
              </h2>
              <p className="mt-5 text-gray-400 leading-relaxed max-w-lg">
                A practical, no-jargon handbook covering everything from SIPs and tax-saving to retirement and asset
                allocation — distilled from two decades of institutional experience.
              </p>

              <ul className="mt-7 space-y-2.5">
                {HIGHLIGHTS.map((h, i) => (
                  <li
                    key={i}
                    data-testid={`guide-highlight-${i}`}
                    className="flex items-start gap-3 text-sm text-gray-300"
                  >
                    <ShieldCheck size={16} className="text-[#D4AF37] mt-0.5 shrink-0" /> {h}
                  </li>
                ))}
              </ul>

              <div className="mt-8 flex flex-wrap items-center gap-4">
                <a
                  href={PDF_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="guide-download-button"
                  aria-label="Open Axomind Investor Education Guide PDF"
                  className="group inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-[#D4AF37] text-black font-semibold hover:bg-[#FFD700] transition shadow-[0_15px_45px_-10px_rgba(212,175,55,0.55)]"
                >
                  <Download size={16} /> Open Guide (PDF)
                </a>
                <a
                  href={PDF_URL}
                  download="Axomind-Investor-Education-Guide.pdf"
                  data-testid="guide-save-button"
                  aria-label="Download PDF to device"
                  className="inline-flex items-center gap-2 px-5 py-3.5 rounded-full bg-white/5 border border-white/15 text-gray-200 font-medium hover:border-[#D4AF37]/60 hover:text-white transition"
                >
                  Save to device
                </a>
                <div className="text-xs text-gray-500 flex items-center gap-2">
                  <FileText size={14} /> Instant download &middot; No sign-up required
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="lg:col-span-5"
            >
              <div className="relative mx-auto w-full max-w-sm rotate-[-3deg] hover:rotate-0 transition-transform duration-500 float-y">
                <div className="aspect-[4/5] rounded-2xl bg-gradient-to-br from-[#0a0a0a] to-[#1a1a1a] border border-[#D4AF37]/40 shadow-[0_30px_80px_rgba(0,0,0,0.6)] p-8 flex flex-col justify-between">
                  <div>
                    <div className="text-xs tracking-[0.3em] text-[#D4AF37]">AXOMIND</div>
                    <div className="text-[10px] text-gray-500 mt-1 tracking-widest">INVESTOR EDUCATION GUIDE</div>
                  </div>
                  <div>
                    <BookOpen size={56} className="text-[#D4AF37]" />
                    <h3 className="font-display font-bold text-3xl text-white mt-5 leading-tight">
                      Intelligent Investing.
                      <span className="text-gold-gradient block">Enduring Results.</span>
                    </h3>
                  </div>
                  <div className="text-[10px] text-gray-600 tracking-widest border-t border-white/10 pt-3">
                    EDITION 2025 • 10+ PAGES
                  </div>
                </div>
                <div className="absolute -bottom-3 -right-3 w-full h-full rounded-2xl bg-[#D4AF37]/10 -z-10" />
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
